import React, { useState, useCallback } from 'react';

import TooltipConstants from '../../constants/tooltips';

import './styles/ModelPricing.css';
import openAiLogo from './styles/openAiLogo.png';
import googleLogo from './styles/googleLogo.png'; 

const models = [
    {
        name: 'Gemini 3.6 Flash',
        inputPerToken: 0.0000015,
        outputPerToken: 0.0000075,
        description: `Leading Gemini model.`,
        goodFor: 'Very fast, reasonably cutting edge performance',
        badFor: 'There are more intillgent models out there',
        image: googleLogo,
    },
    {
        name: 'Gemini 3.5 Flash Lite',
        inputPerToken: 0.0000003,
        outputPerToken: 0.0000025,
        description: "One of the most inexpensive model's on the market",
        goodFor: 'Fastest model on the market, economical',
        badFor: 'Complex tasks',
        image: googleLogo,
    },
    {
        name: 'GPT 5.6 Luna',
        inputPerToken: 0.0000002,
        outputPerToken: 0.0000012,
        description: 'Competent economical model',
        goodFor: 'Very affordable model. Very fast',
        badFor: 'Not as good as Gemini 3.5 Flash while in the same price range.',
        image: openAiLogo,
    },
    {
        name: 'GPT 5.6 Terra',
        inputPerToken: 0.000002,
        outputPerToken: 0.000012,
        description: 'Competent mid-tier model',
        image: openAiLogo,
    },
    {
        name: 'GPT 5.6 Sol',
        inputPerToken: 0.000005,
        outputPerToken: 0.00003,
        description: 'OpenAi\'s leading publicly available model.',
        goodFor: 'One of the better models out there at decent price point',
        badFor: 'Tolerable latency',
        image: openAiLogo,
    },
    {
        name: 'Gemini 3.5 Flash',
        inputPerToken: 0.0000015,
        outputPerToken: 0.000009,
        description: 'Very competent while more economical than GPT 5.6 Sol',
        goodFor: 'Good responses at a reduced price. fast.',
        image: googleLogo,
    }
];

const cheapestModel = models.reduce((min, model) => (model.inputPerToken < min.inputPerToken ? model : min), models[0]);

const ModelPricing = () => {
    const [priceDisplay, setPriceDisplay] = useState('perMillion'); // 'perToken', 'perMillion', 'multiplier'

    const formatPrice = useCallback(
        (price) => {
            if (priceDisplay === 'perToken') {
                return `$${price.toFixed(9)}/Token`;
            } else if (priceDisplay === 'perMillion') {
                const pricePerMillion = price * 1000000;
            
                let formattedPrice;
                if (pricePerMillion < 0.0999) { // don't round down sub 10 cent prices
                    formattedPrice = pricePerMillion.toFixed(3);
                    //Remove trailing zeros
                    formattedPrice = formattedPrice.replace(/(\.0*|0+)$/, '');
                } else {
                    formattedPrice = pricePerMillion.toFixed(2);
                }
            
                return `$${formattedPrice}/Million Tokens`;
            } else if (priceDisplay === 'multiplier') {
                const multiplier = price / cheapestModel.inputPerToken;
                return Number.isInteger(multiplier) ? `${multiplier}x` : `${multiplier.toFixed(2)}x`;
            } else {
                return `$${price.toFixed(9)}/Token`;
            }
        },
        [priceDisplay, cheapestModel])
    ;

    const handleToggle = useCallback((event) => {
        setPriceDisplay(event.target.value);
    }, []);

    return (
        <div className="models-container">
            <h2>Available models</h2>
            <p>
                <b>Note:</b> A margin of around 1% will be applied when topping up your balance to pay for hosting costs. However in beta no such margin is applied.
            </p>
            <p>
                With additional features enabled its not just your original prompt that will be input, you can see the pricing of any enabled features in settings.
            </p>
            <p>
              Contained below are our recommendations and opinions, you should look up external sources. <a href='https://artificialanalysis.ai/models'>artificialanalysis.ai</a> is a good, comprehensive place to start.
            </p>
            
            <div className="toggle-container">
                <label
                  data-tooltip-id="tooltip"
                  data-tooltip-html={TooltipConstants.perToken}
                  data-tooltip-place="bottom"
                >
                    <input
                        type="radio"
                        value="perToken"
                        checked={priceDisplay === 'perToken'}
                        onChange={handleToggle}
                    />
                    Per Token
                </label>
                <label
                  data-tooltip-id="tooltip"
                  data-tooltip-html={TooltipConstants.perMillionToken}
                  data-tooltip-place="bottom"
                >
                    <input
                        type="radio"
                        value="perMillion"
                        checked={priceDisplay === 'perMillion'}
                        onChange={handleToggle}
                    />
                    Per Million Tokens
                </label>
                <label
                  data-tooltip-id="tooltip"
                  data-tooltip-html={TooltipConstants.byCheapest}
                  data-tooltip-place="bottom"
                >
                    <input
                        type="radio"
                        value="multiplier"
                        checked={priceDisplay === 'multiplier'}
                        onChange={handleToggle}
                    />
                    Multiplier of Cheapest
                </label>
            </div>

            {models.map((model) => (
                <div className="model" key={model.name}>
                    <div className="model-header">
                      <img src={model.image} alt={`${model.name} Logo`} className="model-logo" />
                      <h3 className="model-name">{model.name}</h3>
                    </div>
                    
                    <div className="centered model-header">
                        <p>
                            Input: <span className="cost input-cost">{formatPrice(model.inputPerToken, model)}</span>
                        </p>
                        <p>
                            Output: <span className="cost output-cost">{formatPrice(model.outputPerToken, model)}</span>
                        </p>
                    </div>
                    <p className="description">{model.description}</p>
                    <div className="good-bad">
                        {model.goodFor && (
                            <div className="good">
                                <strong>+</strong> {model.goodFor}
                            </div>
                        )}
                        {model.badFor && (
                            <div className="bad">
                                <strong>-</strong> {model.badFor}
                            </div>
                        )}
                    </div>
                </div>
            ))}

            <small>
              Prices last updated: 11th of August
            </small>
        </div>
    );
};

export default ModelPricing;
